import { useQuery } from "@tanstack/react-query";
import type { Pead, PriceDaily } from "epsm-api-types/models";
import type React from "react";
import { useRef } from "react";
import { Spinner } from "@/components/Spinner";
import { Watermark } from "@/shared/components/Watermark";
import { getPriceDataParams } from "@/shared/helpers/getPriceDataParams";
import config from "../../../config";
import { filterDriftsDependingOnQtr } from "../../../lib/helpers/filterDriftsDependsOnQtr";
import { getSeriesDataForAllPeads } from "../../../lib/helpers/getSeriesDataForAllPeads/getSeriesDataForAllPeads";
import { useCreateAllPeadChart } from "../../../lib/hooks/useCreateAllPeadChart";
import peadApi from "../../../lib/services/api";
import { UsePriceData } from "../../../lib/services/UsePriceData";
import type { PeadFilterType } from "../../../types";

type Props = {
	data: Pead[];
	widgetId: string;
	ticker: string;
	peadFilter: PeadFilterType;
	qtr: string;
};

export const AllPeads: React.FC<Props> = ({
	data,
	widgetId,
	ticker,
	peadFilter,
	qtr,
}) => {
	const svgRef = useRef(null);
	const { execute: executePrices } = UsePriceData.create().useGetPrices();
	const { priceResponseFieldsForAll } = config;

	const timeRange = data.reduce(
		(acc, pead) => {
			const ed = pead.earning_day.getTime();
			const nextEd = pead.next_earning_day.getTime();

			return [Math.min(acc[0], ed), Math.max(acc[1], nextEd)];
		},
		[Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY],
	);

	const dataParams = getPriceDataParams(ticker, timeRange[0], timeRange[1]);

	const pricesOptions = peadApi.getQueryOptions<PriceDaily>(
		widgetId,
		ticker,
		dataParams,
		executePrices,
		priceResponseFieldsForAll,
	);

	const {
		data: price = [],
		isLoading,
		isPlaceholderData,
	} = useQuery(pricesOptions);

	const filteredData = filterDriftsDependingOnQtr(
		data,
		qtr?.startsWith("20"),
		peadFilter,
	);

	const onlyTypeBased = filteredData.filter(
		(pead) => pead.drift_unit.name === "type_based",
	);
	const filteredPrice = price.filter(
		(p) =>
			filteredData[filteredData.length - 1].earning_day.getTime() <=
			new Date(p.date).getTime(),
	);

	let { result: series, maxXvalue } = getSeriesDataForAllPeads(
		onlyTypeBased,
		filteredPrice,
	);

	series = series.filter((point) => point.adj_close !== undefined);
	useCreateAllPeadChart(series, svgRef, maxXvalue);

	return (
		<div className="relative max-h-[500px]">
			<div
				className="w-full h-full max-h-[400px] relative"
				data-testid="all-chart"
			>
				{isLoading || isPlaceholderData ? (
					<div className="w-full h-full position-absolute top-0 left-0">
						<Spinner />
					</div>
				) : (
					<svg
						className="border-2 border-gray"
						data-testid="all-peads-chart"
						ref={svgRef}
					/>
				)}

				<Watermark className="absolute right-[10px] bottom-[20px] p-2" />
			</div>
		</div>
	);
};
